// Recording what people do. Every change is an event written locally and queued for sync.
import { encodeTime, monotonicFactory } from 'ulid'
import type { ClientEvent } from '../../shared/events'
import { db, type StoredEvent } from './db'
import { deviceId } from './device'
import { usePack } from './pack'

/** Ids strictly increase even within one millisecond, so this phone's edits keep their order. */
const ulid = monotonicFactory()

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/**
 * A ULID-shaped id that is the same wherever it's made: `time` for the first 10 characters and a
 * SHA-256 of `key` for the other 16. An event that must happen once (a day's goal reward) gets one,
 * so a second app instance (two tabs, the PWA plus a tab, another phone) writes the same event
 * again instead of a second one: IndexedDB overwrites it and the server ignores the repeat (L2-11).
 */
export async function stableId(key: string, time: number): Promise<string> {
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(key)))
  let rest = ''
  for (let i = 0; i < 16; i++) rest += CROCKFORD[hash[i]! % 32]
  return encodeTime(time, 10) + rest
}

type Listener = () => void
const listeners = new Set<Listener>()

/** Sync subscribes so it can push soon after a change. */
export function onRecorded(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export async function record(type: string, payload: unknown, personId: string, now = Date.now(), id?: string): Promise<StoredEvent> {
  // Every event remembers the trip it was made in (D1; domain/tripScope).
  const tripId = usePack.getState().pack?.meta.id
  // A stable id that's already here (another tab made it, maybe synced): keep that one.
  const had = id ? await db.events.get(id) : undefined
  if (had) return had
  const event: ClientEvent = { id: id ?? ulid(now), type, at: now, deviceId: await deviceId(), payload, ...(tripId ? { tripId } : {}) }
  const stored: StoredEvent = { ...event, personId }
  await db.transaction('rw', db.events, db.outbox, async () => {
    await db.events.put(stored)
    await db.outbox.put({ ...event, personId })
  })
  listeners.forEach((l) => l())
  return stored
}
